"use client";

import { useState } from "react";
import Link from "next/link";
import {
  HiSearch,
  HiCheckCircle,
  HiXCircle,
  HiInformationCircle,
  HiQuestionMarkCircle,
  HiRefresh,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ScoreItem {
  label: string;
  points: number;
  max: number;
  passed: boolean;
  tip: string;
}

interface ScoreResult {
  total: number;
  items: ScoreItem[];
}

const EMOTIONAL_WORDS = [
  "shocking",
  "unbelievable",
  "incredible",
  "amazing",
  "terrible",
  "disgusting",
  "hilarious",
  "disturbing",
  "insane",
  "wild",
  "crazy",
  "devastating",
  "brilliant",
  "genius",
  "awful",
  "absurd",
  "heartbreaking",
  "inspiring",
  "jaw-dropping",
  "epic",
];

const QUESTION_WORDS = [
  "what",
  "why",
  "how",
  "when",
  "where",
  "who",
  "which",
  "is",
  "are",
  "can",
  "does",
  "should",
  "would",
  "could",
];

function analyzeTitle(title: string): ScoreResult {
  const items: ScoreItem[] = [];
  const len = title.length;
  const lower = title.toLowerCase();
  const words = title.trim().split(/\s+/);

  // 1. Length score (0–25 pts): ideal 60–80 chars
  let lengthScore = 0;
  if (len >= 60 && len <= 80) lengthScore = 25;
  else if (len >= 45 && len <= 100) lengthScore = 18;
  else if (len >= 20 && len <= 120) lengthScore = 10;
  else if (len > 0) lengthScore = 5;

  items.push({
    label: "Title Length",
    points: lengthScore,
    max: 25,
    passed: lengthScore >= 18,
    tip:
      len < 45
        ? "Your title is too short. Aim for 60–80 characters for best engagement."
        : len > 100
          ? "Your title is quite long. Titles over 100 characters tend to get fewer clicks."
          : "Good length. The ideal range is 60–80 characters.",
  });

  // 2. Question format (0–20 pts)
  const isQuestion = title.trim().endsWith("?");
  const startsWithQuestion = QUESTION_WORDS.some(
    (w) => lower.startsWith(w + " ") || lower.startsWith(w + ",")
  );
  const questionScore =
    isQuestion && startsWithQuestion ? 20 : isQuestion || startsWithQuestion ? 12 : 0;
  items.push({
    label: "Question Format",
    points: questionScore,
    max: 20,
    passed: questionScore >= 12,
    tip: isQuestion
      ? "Questions naturally invite engagement and drive comments."
      : "Consider framing this as a question to encourage more discussion.",
  });

  // 3. Emotional words (0–15 pts)
  const emotionalMatches = EMOTIONAL_WORDS.filter((w) => lower.includes(w));
  const emotionalScore = Math.min(emotionalMatches.length * 8, 15);
  items.push({
    label: "Emotional Words",
    points: emotionalScore,
    max: 15,
    passed: emotionalScore > 0,
    tip:
      emotionalScore > 0
        ? `Found: "${emotionalMatches.slice(0, 3).join('", "')}". Emotional language triggers curiosity and sharing.`
        : "Adding one emotionally charged word can significantly increase click-through rates.",
  });

  // 4. Contains a number (0–15 pts)
  const hasNumber = /\d/.test(title);
  const numberScore = hasNumber ? 15 : 0;
  items.push({
    label: "Contains a Number",
    points: numberScore,
    max: 15,
    passed: hasNumber,
    tip: hasNumber
      ? "Numbers add specificity and credibility — great for list posts and stats."
      : "Including a specific number (e.g. '5 reasons', '2 hours', '$1,000') boosts credibility.",
  });

  // 5. Brackets or parentheses (0–10 pts)
  const hasBrackets = /[\[\(]/.test(title);
  const bracketScore = hasBrackets ? 10 : 0;
  items.push({
    label: "Brackets or Parentheses",
    points: bracketScore,
    max: 10,
    passed: hasBrackets,
    tip: hasBrackets
      ? "Brackets like [Discussion] or (Update) set context and can help with subreddit conventions."
      : "Consider adding [brackets] to indicate content type (e.g. [Question], [Update], [OC]).",
  });

  // 6. Excessive caps penalty (0 to -15 pts)
  const upperCount = (title.match(/[A-Z]/g) || []).length;
  const capsRatio = words.length > 0 ? upperCount / title.replace(/\s/g, "").length : 0;
  const allCapsWords = words.filter(
    (w) => w.length > 2 && w === w.toUpperCase() && /[A-Z]/.test(w)
  );
  let capsPenalty = 0;
  if (allCapsWords.length >= 3) capsPenalty = -15;
  else if (allCapsWords.length >= 2) capsPenalty = -8;
  else if (capsRatio > 0.6) capsPenalty = -10;
  const capsScore = 15 + capsPenalty; // display as 15 max, subtract

  items.push({
    label: "No Excessive Caps",
    points: Math.max(0, capsScore),
    max: 15,
    passed: capsPenalty === 0,
    tip:
      capsPenalty < 0
        ? "TITLES IN ALL CAPS look like shouting and reduce credibility. Use standard sentence or title case."
        : "Good capitalization style — not overly aggressive.",
  });

  const total = Math.max(
    0,
    Math.min(
      100,
      items.reduce((s, i) => s + i.points, 0)
    )
  );

  return { total, items };
}

function ScoreBar({ value, max }: { value: number; max: number }) {
  const pct = Math.round((value / max) * 100);
  return (
    <div className="w-full bg-muted rounded-full h-2">
      <div
        className="h-2 rounded-full transition-all duration-500 bg-primary"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ScoreMeter({ score }: { score: number }) {
  let color = "text-red-500";
  let label = "Needs Work";
  if (score >= 80) {
    color = "text-green-600";
    label = "Excellent";
  } else if (score >= 60) {
    color = "text-yellow-500";
    label = "Good";
  } else if (score >= 40) {
    color = "text-orange-500";
    label = "Fair";
  }

  return (
    <div className="flex flex-col items-center justify-center p-6">
      <div className={`text-7xl font-black tracking-tighter ${color}`}>{score}</div>
      <div className={`text-lg font-bold mt-1 ${color}`}>{label}</div>
      <div className="text-xs text-muted-foreground mt-1">out of 100</div>
      <div className="w-full mt-4 bg-muted rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all duration-700 ${
            score >= 80
              ? "bg-green-500"
              : score >= 60
                ? "bg-yellow-400"
                : score >= 40
                  ? "bg-orange-400"
                  : "bg-red-500"
          }`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

const FAQ_ITEMS = [
  {
    question: "How is the score calculated?",
    answer:
      "The score is calculated across six criteria: title length (25 pts), question format (20 pts), emotional words (15 pts), containing a number (15 pts), brackets or parentheses (10 pts), and a penalty for excessive capitalization (up to -15 pts). The maximum possible score is 100.",
  },
  {
    question: "What is the ideal Reddit title length?",
    answer:
      "Research on Reddit engagement consistently shows that titles in the 60–80 character range tend to perform best. They are long enough to convey specificity but short enough to be read at a glance in a busy feed.",
  },
  {
    question: "Do question titles really get more upvotes?",
    answer:
      "Generally yes. Question-format titles invite responses and naturally generate more comments. More comments signals quality to the Reddit algorithm, which in turn drives more upvotes. This is especially true in discussion-focused subreddits.",
  },
  {
    question: "Why are emotional words important for Reddit titles?",
    answer:
      "Emotional words create curiosity and urgency, which makes readers more likely to click and engage. On a fast-moving platform like Reddit, titles that trigger an emotional reaction are more likely to stop the scroll.",
  },
  {
    question: "Should I always aim for a high score?",
    answer:
      "The score is a guide, not a rule. Different subreddits have different cultures — some communities (e.g. r/science) prefer neutral, factual titles, while entertainment subreddits reward emotional or humorous phrasing. Always read the room for the specific subreddit you are posting to.",
  },
];

export default function RedditTitleAnalyzerClient() {
  const [title, setTitle] = useState("");
  const [result, setResult] = useState<ScoreResult | null>(null);

  const handleAnalyze = () => {
    if (!title.trim()) return;
    setResult(analyzeTitle(title));
  };

  const handleReset = () => {
    setTitle("");
    setResult(null);
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Reddit Title Analyzer"
        description="Score your Reddit post title 0-100 across key engagement factors and get tips to improve before posting."
        url="https://plugmonkey.xyz/tool/reddit-title-analyzer/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Reddit Title Analyzer",
            url: "https://plugmonkey.xyz/tool/reddit-title-analyzer/",
          },
        ]}
        faq={FAQ_ITEMS}
      />

      {/* Hero */}
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-orange-500/10 rounded-full text-orange-500">
          <HiSearch className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Reddit Title Analyzer</h1>
          <p className="text-muted-foreground mt-1">
            Score your post title 0–100 and get tips to improve before you post.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          On Reddit, your title is everything. A bad title on a great post gets ignored. A great
          title on a mediocre post goes viral. This tool scores your title across the factors that
          consistently correlate with higher engagement: length, question format, emotional
          language, numbers, and formatting cues.
        </p>
        <p>Paste your title, see the breakdown, tweak it, and post with confidence.</p>
      </div>

      {/* Input */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Your Reddit Title</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <textarea
                id="reddit-title"
                name="reddit-title"
                className="w-full min-h-[100px] rounded-lg border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                placeholder="Paste or type your Reddit post title here..."
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setResult(null);
                }}
                aria-label="Reddit post title to analyze"
                maxLength={300}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{title.length} characters</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    <HiRefresh className="mr-1 h-4 w-4" /> Reset
                  </Button>
                  <Button onClick={handleAnalyze} disabled={!title.trim()} size="sm">
                    Analyze Title
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Breakdown */}
          {result && (
            <Card>
              <CardHeader>
                <CardTitle>Score Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {result.items.map((item) => (
                  <div key={item.label} className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {item.passed ? (
                          <HiCheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <HiXCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <div className="font-semibold text-sm">{item.label}</div>
                          <div className="text-xs text-muted-foreground flex items-start gap-1 mt-0.5">
                            <HiInformationCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                            <span>{item.tip}</span>
                          </div>
                        </div>
                      </div>
                      <span className="text-sm font-mono font-bold whitespace-nowrap flex-shrink-0">
                        {item.points}/{item.max}
                      </span>
                    </div>
                    <ScoreBar value={item.points} max={item.max} />
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Score sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-primary/5 border-primary/20 shadow-lg overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-primary to-orange-500" />
              <CardContent>
                {result ? (
                  <ScoreMeter score={result.total} />
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <HiSearch className="w-12 h-12 mb-3 opacity-20" />
                    <p className="text-sm text-center">
                      Enter a title and click Analyze to see your score
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-2">Monitor Reddit mentions?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                <strong>Scout Pro for Reddit</strong> notifies you when your keywords, brand, or
                competitors appear in new Reddit posts and comments.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/product/reddit-scout-pro/">View Extension</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={FAQ_ITEMS} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
